<template>
    <div v-if="showIcon" class="position-relative">
        <div class="clickable text-primary hover-text-white" :style="showLabel ? null : 'line-height:0px; padding-left:1px;'" @click.prevent.stop="clickToggleFavorite">
            <span v-if="showLabel && label" class="me-1">{{ label }}</span>
            <spinner v-if="loading" size="sm"/>
            <heart-icon-filled v-else-if="isFavorite" :size="size"/>
            <heart-icon-empty v-else :size="size"/>
        </div>

        <animated-hearts :size="100" class="position-absolute bottom-0 end-0" v-if="showHearts"/>

        <modal v-if="showLoginModal" :open="loginModalOpen" @close="closeLoginModal">
            <template #default>
                <div class="text-center p-5" v-if="loggedInUserId">
                    <spinner size="lg"/>
                </div>
                <login-signup v-else :inline="true" :quickSignup="true" :showLoginForm="false" :showSignupForm="false" signupType="traveler" buttonsCssClass="pb-5 pt-3" instructionsCssClass="mx-auto w-80" instructions="Please login or sign up to save this property to your “favorites”, which can be accessed through your account"/>
            
            </template>
        </modal>
    </div>
    <div v-else-if="showLabel">
        <div class="text-primary">
            <span v-if="label" class="me-1">{{ label }}</span>
            <spinner size="sm"/>
        </div>
    </div>
</template>

<script type="text/javascript">
    import HeartIconEmpty from '@/assets/icons/heart.svg.vue';
    import HeartIconFilled from '@/assets/icons/heart-filled.svg.vue';
    import AnimatedHearts from '@/components/Content/AnimatedHearts.vue';
    import LoginSignup from '@/components/Content/Auth/LoginSignup.vue';
    import CommonComponents from '@/components/Common.vue';

    import ApiObject from '@/classes/ApiObject.js';
    import Api from '@/classes/Api.js';
    import { UseApiStore } from '@/store/Api.js';
    import { UseAuthStore } from '@/store/Auth.js';

    export default {

        components: {
            HeartIconEmpty,
            HeartIconFilled,
            AnimatedHearts,
            LoginSignup,
            ...CommonComponents,
        },

        setup() {
            return {
                apiStore: UseApiStore(),
                authStore: UseAuthStore(),
            };
        },
        
        data() {
            return {
                showLoginModal: false,
                loginModalOpen: true,
                loggedInUserId: null,
                showHearts: false,
                loading: false,

                pendingAddtoFav: false,
            };
        },

        props: {
            property: {
                type: ApiObject,
                required: true,
            },

            size: {
                type: Number,
                default: 18
            },

            showLabel: {
                type: Boolean,
                default: false,
            },

            saveLabel: {
                type: String,
                default: 'Save',
            },

            savedLabel: {
                type: String,
                default: 'Saved',
            },
        },

        emits: ['updated', 'click'],

        created() {
            if (this.authStore.loggedInUser) {
                this.loggedInUserId = this.authStore.loggedInUser.id;
                this.requestIfNeeded();
            }

            // we'll watch user id change
            // so we can refresh the content as needed
            this.$watch(
                () => this.authStore.loggedInUser,

                (function(newVal) {
                    if (
                        (newVal) &&
                        (newVal.id)
                    ) {
                        if (newVal.id != this.loggedInUserId) {
                            this.loggedInUserId = newVal.id;
                        }
                    }
                    else {
                        this.loggedInUserId = null;
                    }
                }.bind(this)),

                { deep: true }
            );
        },

        watch: {
            loggedInUserId() {
                if (this.loggedInUserId) {
                    this.loading = true;
                    this.loginModalOpen = false;
                    this.requestIfNeeded();
                }
            },
        },

        computed: {

            showIcon() {
                if ((this.userFavorites) || (this.loading)) {
                    return true;
                }
                if (
                    (this.authStore.initialPull) &&
                    (!this.loggedInUserId)
                ) {
                    return true;
                }

                return false;
            },

            requestUrl() {
                if (!this.loggedInUserId) return null;

                let url = 'travelers/properties/favorites?u='+this.loggedInUserId;
                return url;
            },

            userFavorites() {
                if (!this.requestUrl) return null;
                let response = this.apiStore.requests[this.requestUrl];
                
                if (!response) {
                    return null;
                }

                if ((response.valid) && (response.results) && (!response.results.error)) {
                    if (this.pendingAddtoFav) {
                        this.addToFavOnLogin();
                    }
                    return response.results
                }
                
                return null;
            },

            isFavorite() {
                if (
                    (this.userFavorites) &&
                    (this.userFavorites.favorites) &&
                    (this.userFavorites.favorites.length)
                ) {
                    for (let i=0; i<this.userFavorites.favorites.length; i++) {
                        if (this.userFavorites.favorites[i].propertyId == this.property.id) {
                            return true;
                        }
                    }
                }
                return false;
            },

            label() {
                if (this.isFavorite) {
                    
                    return this.savedLabel;
                }
                return this.saveLabel;
            },
        },

        methods: {
            closeLoginModal() {
                this.showLoginModal = false;
                this.loginModalOpen = true;
            },

            requestIfNeeded() {
                if (!this.userFavorites) {
                    this.requestData();
                }
                else {
                    this.showHearts = false;
                    this.loading = false;
                }
            },

            async requestData(fresh) {

                if (!this.loggedInUserId) {
                    this.showHearts = false;
                    this.loading = false;
                    return null;
                }

                let payload = {
                    url: this.requestUrl,
                    forever: true,
                };

                if (fresh) payload.fresh = true;
                
                await this.apiStore.get(payload);

                this.showHearts = false;
                this.loading = false;
            },

            addToFavOnLogin() {
                this.pendingAddtoFav = false;
                requestAnimationFrame(function() {
                    if (!this.isFavorite) {
                        this.toggleFavorite();
                    }
                }.bind(this));
            },

            clickToggleFavorite() {
                this.toggleFavorite();
                this.$emit('click');
            },

            async toggleFavorite() {
                if (this.loggedInUserId) {
                    
                    if (!this.isFavorite) {
                        this.showHearts = true;
                    }
                    this.loading = true;
                    await Api.post('travelers/properties/favorites/'+this.property.id);
                    this.requestData(true);
                }
                else {
                    this.pendingAddtoFav = true;
                    this.loginModalOpen = true;
                    this.showLoginModal = true;
                }
            },
        }
    };

</script>
